import React, { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, Tab, Box } from "@mui/material";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";

const DetailedCampaign = ({ startDate, endDate, token }) => {
  console.log("DetailedCampaign", startDate, endDate);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // const [pageSize, setPageSize] = useState(5);

  // Add this new state for tracking expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Add this function to handle row expansion
  const handleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  // Add this function to filter rows
  const getVisibleRows = (rows) => {
    if (!rows) return [];
    return rows.filter(
      (row) => row.parent_id === null || expandedRows.has(row.parent_id),
    );
  };

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/reports/CallCentreSalesReports/DetailedCampaignSBIReport?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Transform the data
        const transformedData = [];
        let currentId = 1;

        response.data.data.forEach((item) => {
          // Add main row
          const mainRow = {
            ...item,
            id: currentId,
            parent_id: null,
          };

          // Remove campaign_detail_jsonb from main row
          delete mainRow.campaign_detail_jsonb;
          transformedData.push(mainRow);

          // Add child rows from campaign_detail_jsonb
          if (
            item.campaign_detail_jsonb &&
            Array.isArray(item.campaign_detail_jsonb)
          ) {
            item.campaign_detail_jsonb.forEach((detail) => {
              transformedData.push({
                ...detail,
                id: ++currentId,
                parent_id: mainRow.id,
                campaign_group: detail.campaign, // Use campaign as campaign_group for child rows
              });
            });
          }

          currentId++;
        });

        setData({ ...response.data, data: transformedData });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, token]);

  // Define columns for the three tabs
  const salesColumns = [
    {
      field: "campaign_group",
      headerName: "",
      flex: 1,
    },
    { field: "target_sales", headerName: "Target Sales", flex: 1 },
    { field: "total_sales", headerName: "Total Sales", flex: 1 },
    { field: "sales", headerName: "Sales", flex: 1 },
    { field: "incomplete", headerName: "Incomplete", flex: 1 },
    { field: "pended", headerName: "Pended", flex: 1 },
    { field: "staged", headerName: "Staged", flex: 1 },
    { field: "cancelled", headerName: "Cancelled", flex: 1 },
    // calculated columns
    // total sales % target = (total_sales / target_sales) * 100
    {
      field: "total_sales_percentage",
      headerName: "Total Sales % Target",
      flex: 1,
      valueGetter: (params) => {
        const totalSales = params.row.total_sales || 0;
        const targetSales = params.row.target_sales;
        // Avoid division by zero
        if (targetSales === 0 || !targetSales) return "";
        return ((totalSales / targetSales) * 100).toFixed(0) + "%";
      },
    },
    // sales % target = (sales / target_sales) * 100
    {
      field: "sales_percentage",
      headerName: "Sales % Target",
      flex: 1,
      valueGetter: (params) => {
        const sales = params.row.sales || 0;
        const targetSales = params.row.target_sales;
        // Avoid division by zero
        if (targetSales === 0 || !targetSales) return "";
        return ((sales / targetSales) * 100).toFixed(0) + "%";
      },
    },
    // incomplete sales % total sales = (incomplete / total_sales) * 100
    {
      field: "incomplete_percentage",
      headerName: "Incomplete % Total Sales",
      flex: 1,
      valueGetter: (params) => {
        const incomplete = params.row.incomplete || 0;
        const totalSales = params.row.total_sales;
        // Avoid division by zero
        if (totalSales === 0 || !totalSales) return "";
        return ((incomplete / totalSales) * 100).toFixed(0) + "%";
      },
    },
    // pended sales % total sales = (pended / total_sales) * 100
    {
      field: "pended_percentage",
      headerName: "Pended % Total Sales",
      flex: 1,
      valueGetter: (params) => {
        const pended = params.row.pended || 0;
        const totalSales = params.row.total_sales;
        // Avoid division by zero
        if (totalSales === 0 || !totalSales) return "";
        return ((pended / totalSales) * 100).toFixed(0) + "%";
      },
    },
    // staged sales % total sales = (staged / total_sales) * 100
    {
      field: "staged_percentage",
      headerName: "Staged % Total Sales",
      flex: 1,
      valueGetter: (params) => {
        const staged = params.row.staged || 0;
        const totalSales = params.row.total_sales;
        // Avoid division by zero
        if (totalSales === 0 || !totalSales) return "";
        return ((staged / totalSales) * 100).toFixed(0) + "%";
      },
    },
    // cancelled sales % total sales = (cancelled / total_sales) * 100
    {
      field: "cancelled_percentage",
      headerName: "Cancelled % Total Sales",
      flex: 1,
      valueGetter: (params) => {
        const cancelled = params.row.cancelled || 0;
        const totalSales = params.row.total_sales;
        // Avoid division by zero
        if (totalSales === 0 || !totalSales) return "";
        return ((cancelled / totalSales) * 100).toFixed(0) + "%";
      },
    },
  ];

  const biColumns = [
    {
      field: "campaign_group",
      headerName: "",
      flex: 1,
    },
    {
      field: "total_bi",
      headerName: "Total BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.total_bi) || 0,
    },
    {
      field: "sales_bi",
      headerName: "Sales BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.sales_bi) || 0,
    },
    {
      field: "incomplete_bi",
      headerName: "Incomplete BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.incomplete_bi) || 0,
    },
    {
      field: "pended_bi",
      headerName: "Pended BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.pended_bi) || 0,
    },
    {
      field: "staged_bi",
      headerName: "Staged BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.staged_bi) || 0,
    },
    {
      field: "cancelled_bi",
      headerName: "Cancelled BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.cancelled_bi) || 0,
    },
  ];

  const hoursColumns = [
    {
      field: "campaign_group",
      headerName: "",
      flex: 1,
    },
    { field: "target_hours", headerName: "Target Hours", flex: 1 },
    {
      field: "campaign_hours",
      headerName: "Campaign Hours",
      flex: 1,
      valueGetter: (params) =>
        parseFloat(params.row.campaign_hours).toFixed(0) || 0,
    },
    // calculated columns
    //campaign_hours % target_hours = (campaign_hours / target_hours) * 100
    {
      field: "campaign_hours_percentage",
      headerName: "Campaign Hours % Target",
      flex: 1,
      valueGetter: (params) => {
        const campaignHours = params.row.campaign_hours || 0;
        const targetHours = params.row.target_hours;
        // Avoid division by zero
        if (targetHours === 0 || !targetHours) return "";
        return ((campaignHours / targetHours) * 100).toFixed(0) + "%";
      },
    },
    {
      field: "target_sales_per_hour",
      headerName: "Target Sales per Hour",
      flex: 1,
    },
    // calculated columns
    // total sales per hour = total_sales / campaign_hours
    {
      field: "total_sales_per_hour",
      headerName: "Total Sales per Hour",
      flex: 1,
      valueGetter: (params) => {
        const sales = params.row.total_sales || 0;
        const campaignHours = params.row.campaign_hours || 1; // Avoid division by zero
        return (sales / campaignHours).toFixed(2);
      },
    },
    // sales per hour = sales / campaign_hours
    {
      field: "sales_per_hour",
      headerName: "Sales per Hour",
      flex: 1,
      valueGetter: (params) => {
        const sales = params.row.sales || 0;
        const campaignHours = params.row.campaign_hours || 1; // Avoid division by zero
        return (sales / campaignHours).toFixed(2);
      },
    },
  ];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  if (!data || data.data.length === 0) {
    return <h3>No Data Available</h3>;
  }

  return (
    <div>
      <Tabs value={tabValue} onChange={handleChange} aria-label="tabs">
        <Tab label="Sales" />
        <Tab label="Hours" />
        <Tab label="BI" />
      </Tabs>

      <Box sx={{ marginTop: 2 }}>
        {tabValue === 0 && data && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...salesColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // checkboxSelection={false}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            pageSizeOptions={[5, 10, 15, 20]}
            getDetailPanelHeight={() => "auto"}
            rowThreshold={0}
          />
        )}
        {tabValue === 1 && data && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...hoursColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            checkboxSelection={false}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            //
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            // pageSizeOptions={[5, 10, 15, 20]}
            getDetailPanelHeight={() => "auto"}
            rowThreshold={0}
          />
        )}
        {tabValue === 2 && data && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...biColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            checkboxSelection={false}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            // pageSizeOptions={[5, 10, 15, 20]}
            getDetailPanelHeight={() => "auto"}
            rowThreshold={0}
          />
        )}
      </Box>
    </div>
  );
};

export default DetailedCampaign;
